import { AlertCircle, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { Input } from "../../../ui/input";
import { Skeleton } from "../../../ui/skeleton";
import { BranchPicker } from "./BranchPicker";
import { ManualUrlFallback } from "./ManualUrlFallback";
import { NoAccountsEmptyState } from "./NoAccountsEmptyState";
import { ProviderTabs } from "./ProviderTabs";
import { RepoList } from "./RepoList";

interface GitAccount {
  id: string;
  provider: string;
  username: string;
  avatarUrl?: string;
  manageUrl?: string;
}

interface GitRepository {
  id: string;
  name: string;
  fullName: string;
  private: boolean;
  defaultBranch: string;
  updatedAt?: string;
  language?: string;
}

interface GitBranchItem {
  name: string;
  isDefault: boolean;
}

type Status =
  | "loading-accounts"
  | "no-accounts"
  | "accounts-error"
  | "ready"
  | "manual";

interface GitRepoSelectorProps {
  value: { url: string; branch: string };
  onChange: (repo: { url: string; branch: string; git_account_id?: string }) => void;
  fillHeight?: boolean;
}

function AccountsSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Skeleton className="h-8 w-24 rounded-md" />
        <Skeleton className="h-8 w-20 rounded-md" />
      </div>
      <Skeleton className="h-9 w-full rounded-md" />
      <div className="bg-card rounded-lg divide-y">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-11 flex items-center px-3 gap-3">
            <Skeleton className="h-4 w-4 shrink-0" />
            <Skeleton className="h-3.5 flex-1" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── API response normalizers ──────────────────────────────────────────────
// The backend returns snake_case wrapped in { data: { ... }, success: true }.
// These helpers unwrap and convert to the camelCase shapes used in this component.

function normalizeAccount(raw: any): GitAccount {
  return {
    id: raw.id,
    provider: raw.provider,
    username: raw.username,
    avatarUrl: raw.avatar_url ?? raw.avatarUrl,
    manageUrl: raw.manage_url ?? raw.manageUrl,
  };
}

function normalizeRepo(raw: any): GitRepository {
  return {
    id: raw.id,
    name: raw.name,
    fullName: raw.full_name ?? raw.fullName ?? "",
    private: !!raw.private,
    defaultBranch: raw.default_branch ?? raw.defaultBranch ?? "main",
    updatedAt: raw.updated_at ?? raw.updatedAt,
    language: raw.language,
  };
}

function normalizeBranch(raw: any): GitBranchItem {
  return {
    name: raw.name,
    isDefault: raw.is_default ?? raw.isDefault ?? false,
  };
}

// Unwrap the API envelope: { data: { ... }, success: true } → inner object
function unwrap(response: any): any {
  return response?.data ?? response;
}

export function GitRepoSelector({ value, onChange, fillHeight = false }: GitRepoSelectorProps) {
  const [status, setStatus] = useState<Status>("loading-accounts");
  const [accounts, setAccounts] = useState<GitAccount[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<string>("");
  const [activeAccount, setActiveAccount] = useState<GitAccount | null>(null);
  const [repos, setRepos] = useState<GitRepository[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredRepos, setFilteredRepos] = useState<GitRepository[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<GitRepository | null>(null);
  const [branches, setBranches] = useState<GitBranchItem[]>([]);
  const [selectedBranch, setSelectedBranch] = useState("main");
  const [branchesLoading, setBranchesLoading] = useState(false);

  // Load accounts on mount
  useEffect(() => {
    async function loadAccounts() {
      try {
        const raw = (await api.git.accounts.list()) as any;
        const list: GitAccount[] = (unwrap(raw)?.accounts ?? []).map(normalizeAccount);
        if (list.length === 0) {
          setStatus("no-accounts");
        } else {
          setAccounts(list);
          setActiveAccountId(list[0].id);
          setActiveAccount(list[0]);
          setStatus("ready");
        }
      } catch {
        setStatus("accounts-error");
      }
    }
    loadAccounts();
  }, []);

  // When the active account settles (accounts load after user already entered manual mode)
  // or when the user switches accounts while in manual mode, backfill git_account_id.
  useEffect(() => {
    if (status === "manual" && value.url) {
      onChange({ url: value.url, branch: value.branch, git_account_id: activeAccountId || undefined });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  // Fetch repos when active account changes
  useEffect(() => {
    if (!activeAccountId || status !== "ready") return;
    setSelectedRepo(null);
    setBranches([]);
    setSelectedBranch("main");
    setSearch("");

    async function loadRepos() {
      setReposLoading(true);
      try {
        const raw = (await api.git.repositories.getByAccount(activeAccountId)) as any;
        const list: GitRepository[] = (unwrap(raw)?.repositories ?? []).map(normalizeRepo);
        setRepos(list);
        setFilteredRepos(list);
      } catch {
        setRepos([]);
        setFilteredRepos([]);
      } finally {
        setReposLoading(false);
      }
    }
    loadRepos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  // Debounced search
  useEffect(() => {
    if (!search.trim() || search.length < 2) {
      setFilteredRepos(repos);
      return;
    }
    const id = setTimeout(async () => {
      setReposLoading(true);
      try {
        const raw = (await api.git.repositories.list({ search })) as any;
        setFilteredRepos((unwrap(raw)?.repositories ?? []).map(normalizeRepo));
      } catch {
        // Fall back to client-side filter on error
        const lower = search.toLowerCase();
        setFilteredRepos(repos.filter((r) => r.fullName.toLowerCase().includes(lower)));
      } finally {
        setReposLoading(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [search, repos]);

  function handleAccountChange(id: string) {
    setActiveAccountId(id);
    setActiveAccount(accounts.find((a) => a.id === id) ?? null);
  }

  async function handleRepoSelect(repo: GitRepository) {
    setSelectedRepo(repo);
    setBranchesLoading(true);
    setBranches([]);

    const defaultBranch = repo.defaultBranch || "main";
    setSelectedBranch(defaultBranch);

    // Compute the repo URL from the active account provider
    const provider = activeAccount?.provider ?? "github";
    const hostMap: Record<string, string> = {
      github: "github.com",
      gitlab: "gitlab.com",
      bitbucket: "bitbucket.org",
    };
    const host = hostMap[provider] ?? "github.com";
    const repoUrl = `https://${host}/${repo.fullName}`;

    onChange({ url: repoUrl, branch: defaultBranch, git_account_id: activeAccountId });

    try {
      const raw = (await api.git.repositories.getBranches(repo.id)) as any;
      const list: GitBranchItem[] = (unwrap(raw)?.branches ?? []).map(normalizeBranch);
      setBranches(list);
      // Use the default branch from API if available
      const def = list.find((b) => b.isDefault);
      if (def) {
        setSelectedBranch(def.name);
        onChange({ url: repoUrl, branch: def.name, git_account_id: activeAccountId });
      }
    } catch {
      // Keep defaultBranch fallback already set
    } finally {
      setBranchesLoading(false);
    }
  }

  function handleBranchChange(branch: string) {
    setSelectedBranch(branch);
    if (selectedRepo && activeAccount) {
      const provider = activeAccount.provider;
      const hostMap: Record<string, string> = {
        github: "github.com",
        gitlab: "gitlab.com",
        bitbucket: "bitbucket.org",
      };
      const host = hostMap[provider] ?? "github.com";
      onChange({ url: `https://${host}/${selectedRepo.fullName}`, branch, git_account_id: activeAccountId });
    }
  }

  async function retryLoadAccounts() {
    setStatus("loading-accounts");
    try {
      const raw = (await api.git.accounts.list()) as any;
      const list: GitAccount[] = (unwrap(raw)?.accounts ?? []).map(normalizeAccount);
      if (list.length === 0) {
        setStatus("no-accounts");
      } else {
        setAccounts(list);
        setActiveAccountId(list[0].id);
        setActiveAccount(list[0]);
        setStatus("ready");
      }
    } catch {
      setStatus("accounts-error");
    }
  }

  const fh = fillHeight;

  return (
    <div className={fh ? "flex flex-col h-full gap-3 min-h-0" : "space-y-3"}>
      <p className={`text-sm font-medium${fh ? " shrink-0" : ""}`}>Source code</p>

      {status === "loading-accounts" && (
        <div className={fh ? "flex-1 overflow-y-auto" : undefined}>
          <AccountsSkeleton />
        </div>
      )}

      {status === "no-accounts" && (
        <div className={fh ? "flex-1" : undefined}>
          <NoAccountsEmptyState onManual={() => setStatus("manual")} />
        </div>
      )}

      {status === "accounts-error" && (
        <div className={`rounded-lg p-4 flex items-start gap-3 text-sm text-danger-fg bg-danger-bg${fh ? " shrink-0" : ""}`}>
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">Couldn't load Git accounts</p>
            <button
              type="button"
              onClick={retryLoadAccounts}
              className="text-xs underline mt-1 hover:no-underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {status === "ready" && (
        <>
          <div className={fh ? "shrink-0" : undefined}>
            <ProviderTabs
              accounts={accounts}
              activeId={activeAccountId}
              onChange={handleAccountChange}
            />
          </div>

          {/* Search */}
          <div className={`relative${fh ? " shrink-0" : ""}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search repositories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>

          <RepoList
            repos={filteredRepos}
            loading={reposLoading}
            selectedRepoId={selectedRepo?.id}
            onSelect={handleRepoSelect}
            fillHeight={fh}
          />

          {/* Only installation-granted repos are listed — point users to
              GitHub's repo-access settings when something is missing. */}
          {activeAccount?.provider === "github" && activeAccount?.manageUrl && (
            <p className={`text-xs text-muted-foreground${fh ? " shrink-0" : ""}`}>
              Don't see a repository?{" "}
              <a
                href={activeAccount.manageUrl}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2 hover:text-foreground"
              >
                Manage repository access on GitHub
              </a>
            </p>
          )}

          {selectedRepo && (
            <div className={fh ? "shrink-0" : undefined}>
              <BranchPicker
                branches={branches}
                value={selectedBranch}
                onChange={handleBranchChange}
                loading={branchesLoading}
              />
            </div>
          )}
        </>
      )}

      {status !== "manual" && status !== "loading-accounts" && (
        <button
          type="button"
          onClick={() => setStatus("manual")}
          className={`text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline${fh ? " shrink-0" : ""}`}
        >
          Enter URL manually →
        </button>
      )}

      {status === "manual" && (
        <div className={fh ? "flex-1 overflow-y-auto" : undefined}>
          <ManualUrlFallback
            value={value}
            onChange={(v) => onChange({ ...v, git_account_id: activeAccountId || undefined })}
            onBack={() => setStatus(accounts.length > 0 ? "ready" : "no-accounts")}
          />
        </div>
      )}
    </div>
  );
}
